<script setup lang="ts">
import type { IProps } from './ManagementTeam.types'
import { useNormalizeText } from '@/composables/useNormalizeText'

defineProps<IProps>()
const localePath = useLocalePath()
const { normalizeText } = useNormalizeText()
</script>

<template>
  <div class="managementTeam">

    <div class="managementTeam__inner">

    <div
      v-if="boardTitle || !hasDivider"
      class="managementTeam__title"
    >
      <UiHeader
        v-if="boardTitle"
        :header="boardTitle"
        :subheader="boardLead"
        position="center"
        header-size="h5"
        header-size-tablet="h5"
        header-size-mobile="h7"
      />
    </div>

    <div
      :class="[
        'managementTeam__grid',
        boardMembers.length <= 1 && 'managementTeam__grid--flex',
        hasDivider && 'managementTeam__grid--divider'
      ]"
    >
      <div
        v-for="(boardMember, index) in boardMembers"
        :key="boardMember.boardMemberName"
        :class="['managementTeam__container', `managementTeam__container--${index}`]"
      >
        <div
          v-if="boardMember.boardMemberPhoto"
          class="managementTeam__assetWrapper"
        >
          <img
            class="managementTeam__asset"
            :src="boardMember.boardMemberPhoto"
            :alt="boardMember.boardMemberName"
            :title="boardMember.boardMemberName"
          >

        </div>
  
        <div
          :class="[
            'managementTeam__box',
            !boardMember.boardMemberPhoto && 'managementTeam__box--rounded',
          ]"
        >
          <p
            class="managementTeam__name"
            v-html="boardMember.boardMemberName.split(' ').join('<br />')"
          />
  
          <p
            class="managementTeam__role"
          >
            {{ boardMember.boardMemberRole }}
          </p>

          <div
            class="managementTeam__linkWrapper"
          >
            <NuxtLink
              class="managementTeam__link"
              :to="localePath({
                name: pathKeyToUrl,
                params: {
                  member: normalizeText(boardMember.boardMemberName.toLowerCase().split(' ').join('-'))
                }
              })"
            >
              {{  $t('investor.corporateGovernance.main.members.label') }}
  
              <img
                class="managementTeam__icon"
                src="/icons/investor/managementTeam/icon.svg"
                :aria-hidden="true"
              >
            </NuxtLink>
          </div>
        </div>
  
      </div>
    </div>
  </div>
</div>
</template>

<style lang="scss" scoped>
.managementTeam {
  padding: 4.8rem 2.4rem;
  background-color: $white;
  @include section-investor();

  &__inner {
    @include section-investor__container();
  }

  &__grid--divider {
    position: relative;

    &::before {
      position: absolute;
      top: calc(-4.8rem - 1px);
      right: 0;
      left: 0;
      height: 1px;
      background-color: $neutral-50;
      content: '';
    }
  }

  &__container {
    padding-bottom: 2.4rem;

    &:last-child {
      padding-bottom: 0;
    }
  }

  &__title {
    @include h7;

    margin-bottom: 6.4rem;
  }

  &__box {
    padding: 2.4rem;
    background-color: $gray-100;
    border-bottom-right-radius: 1.6em;
    border-bottom-left-radius: 1.6em;

    &--rounded {
      border-radius: 1.6rem;
    }
  }

  &__asset {
    display: block;
    width: 100%;
  }

  &__assetWrapper {
    overflow: hidden;
    background-color: $gray-100;
    border-top-left-radius: 1.6rem;
    border-top-right-radius: 1.6rem;
  }

  &__name {
    @include h8;

    padding-bottom: .8rem;
    color: $text-400;
  }

  &__role {
    @include body-1-medium;

    padding-bottom: 1.6rem;
    color: $text-300;
  }

  &__link {
    @include body-3-medium;

    display: flex;
    align-items: center;
    color: $text-400;
  }

  &__linkWrapper {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    height: 100%;
  }

  &__icon {
    margin-left: .8rem;
  }

  @include atXS {
    max-width: 64rem;
    margin: auto;

    &__container {
      display: flex;
      flex-direction: column;
      max-width: 23.4rem;
      min-width: 23.4rem;
      padding: 0;
    }

    &__assetWrapper {
      flex-shrink: 0;
    }

    &__box {
      display: flex;
      flex-grow: 1;
      flex-direction: column;
    }

    &__title {
      @include h5;
    }

    &__grid {
      display: grid;
      justify-content: center;
      grid-template-columns: repeat(2, minmax(0, 23.4rem));
      gap: 2.4rem;
      max-width: 100%;
      width: fit-content;
      margin-right: auto;
      margin-left: auto;

      &--flex {
        display: flex;
        flex-basis: 23.4rem;
      }
    }
  }

  @include atM {
    max-width: unset;
    padding-top: 6.4rem;
    padding-bottom: 6.4rem;

    &__container {
      max-width: 30.6rem;
      min-width: 30.6rem;
    }

    &__grid {
      grid-template-columns: repeat(auto-fit, minmax(0, 30.6rem));

      &--divider::before {
        top: calc(-6.4rem - 1px);
      }
    }
  }
}

</style>
